/**
 * Чистая модель постепенного раскрытия элементов списка («показать ещё»).
 * Без доступа к DOM и без Vue-реактивности — вызывающий код синхронизирует результат во view.
 */
export class ShowMoreRevealModel {
  /**
   * @param {{ enabled: boolean, mobilePageSize: number, desktopPageSize: number }} options
   */
  constructor(options) {
    this.enabled = Boolean(options.enabled);
    this.mobilePageSize = Number(options.mobilePageSize);
    this.desktopPageSize = Number(options.desktopPageSize);
    this.visibleCount = 0;
    this.totalCount = 0;
  }

  /**
   * @param {boolean} isMobile
   * @returns {number}
   */
  resolvePageSize(isMobile) {
    return isMobile ? this.mobilePageSize : this.desktopPageSize;
  }

  /**
   * Обновить опции размеров страницы / флага enabled (из смерженного конфига).
   * @param {{ enabled: boolean, mobilePageSize: number, desktopPageSize: number }} options
   */
  applyOptions(options) {
    this.enabled = Boolean(options.enabled);
    this.mobilePageSize = Number(options.mobilePageSize);
    this.desktopPageSize = Number(options.desktopPageSize);
  }

  /**
   * Сброс на первую страницу для нового отфильтрованного списка.
   * @param {number} totalCount
   * @param {number} pageSize
   */
  resetToFirstPage(totalCount, pageSize) {
    this.totalCount = Math.max(0, Number(totalCount) || 0);
    if (!this.enabled) {
      this.visibleCount = this.totalCount;
      return;
    }
    const safePageSize = Math.max(1, Number(pageSize) || 1);
    this.visibleCount = Math.min(this.totalCount, safePageSize);
  }

  /**
   * Показать следующую страницу с текущим page size viewport.
   * @param {number} pageSize
   */
  revealNextPage(pageSize) {
    if (!this.enabled) {
      this.visibleCount = this.totalCount;
      return;
    }
    const safePageSize = Math.max(1, Number(pageSize) || 1);
    this.visibleCount = Math.min(this.totalCount, this.visibleCount + safePageSize);
  }

  /**
   * После смены page size из‑за viewport:
   * - не уменьшать visibleCount
   * - если visibleCount меньше нового (большего) page size — поднять до него (с потолком total)
   * @param {number} newPageSize
   */
  syncAfterViewportPageSizeChange(newPageSize) {
    if (!this.enabled) {
      this.visibleCount = this.totalCount;
      return;
    }
    const safePageSize = Math.max(1, Number(newPageSize) || 1);
    if (this.visibleCount < safePageSize) {
      this.visibleCount = Math.min(this.totalCount, safePageSize);
    }
  }

  /**
   * Раскрыть столько элементов, чтобы lastIndexInclusive был виден.
   * Никогда не уменьшает visibleCount.
   * @param {number} lastIndexInclusive индекс с нуля в полном списке
   */
  ensureVisibleThroughIndex(lastIndexInclusive) {
    if (!this.enabled) {
      this.visibleCount = this.totalCount;
      return;
    }
    const safeIndex = Math.max(0, Number(lastIndexInclusive) || 0);
    const targetVisibleCount = Math.min(this.totalCount, safeIndex + 1);
    if (this.visibleCount < targetVisibleCount) {
      this.visibleCount = targetVisibleCount;
    }
  }

  /**
   * @returns {boolean}
   */
  shouldShowButton() {
    return this.enabled && this.visibleCount < this.totalCount;
  }

  /**
   * @returns {number}
   */
  getVisibleCount() {
    if (!this.enabled) {
      return this.totalCount;
    }
    return this.visibleCount;
  }
}
